/**
 * El mando tactil a gusto de cada uno, y la geometria que lo coloca.
 *
 * Los juegos de movil que se toman el tactil en serio (PUBG, CoD, FC Mobile)
 * dejan mover y agrandar los botones: un pulgar de 5 cm en un telefono de 6"
 * no llega a donde llega uno en una tablet de 11". Aqui se guarda eso —donde
 * va cada boton, su tamaño, si juegas de zurdo— y si quieres que el movil
 * vibre. Es input puro: nada de esto entra en la simulacion ni en la red.
 *
 * Las posiciones se guardan en UNIDADES DE PULGAR (`thumbUnit`), no en pixeles
 * ni en porcentaje de pantalla: medidas desde la esquina de abajo del lado de
 * los botones y escaladas con el lado corto. Asi el boton que colocaste en el
 * movil cae en el mismo sitio del pulgar en la tablet, en vez de irse al
 * centro (porcentaje) o quedarse pegado a la esquina (pixeles).
 */
export interface ButtonSpot {
  /** Desde el borde del lado de los botones hasta el centro, en unidades de pulgar. */
  x: number;
  /** Desde el borde de abajo hasta el centro, en unidades de pulgar. */
  y: number;
}

export interface TouchPrefs {
  /** Tamaño de los botones, 0,8-1,35. */
  size: number;
  /** Zurdo: joystick a la derecha, botones a la izquierda. */
  leftHanded: boolean;
  /** Que el movil vibre con el partido. */
  vibration: boolean;
  /** Tocar el campo con el balon golpea hacia alli. */
  fieldTouch: boolean;
  /** `null` = donde viene de fabrica. */
  kick: ButtonSpot | null;
  dash: ButtonSpot | null;
}

export const DEFAULT_TOUCH_PREFS: TouchPrefs = {
  size: 1,
  leftHanded: false,
  vibration: true,
  fieldTouch: true,
  kick: null,
  dash: null,
};

/*
 * De fabrica: golpear abajo en la esquina, donde descansa el pulgar, y correr
 * en el arco hacia dentro. NO a media altura del borde: en un movil en
 * horizontal ahi esta la porteria rival, y el boton azul de antes la tapaba.
 */
export const DEFAULT_KICK_SPOT: ButtonSpot = { x: 96, y: 88 };
export const DEFAULT_DASH_SPOT: ButtonSpot = { x: 204, y: 50 };
/** Diametro de fabrica, en unidades de pulgar. */
export const KICK_SIZE = 76;
export const DASH_SIZE = 54;
/** Ancho de la zona del joystick, en fraccion de la pantalla. */
export const STICK_ZONE = 0.42;

const SIZE_MIN = 0.8;
const SIZE_MAX = 1.35;
const STORAGE_KEY = "fynta.touch";

/**
 * Pixeles por unidad de pulgar. 1 en un movil en horizontal (360-430 de
 * alto); crece con la pantalla, pero menos que ella: el pulgar no crece con la
 * tablet, solo el sitio que hay.
 */
export function thumbUnit(width: number, height: number): number {
  const short = Math.min(width, height);
  return Math.min(1.5, Math.max(0.9, short / 390));
}

function validSpot(v: unknown): ButtonSpot | null {
  if (!v || typeof v !== "object") return null;
  const { x, y } = v as { x?: unknown; y?: unknown };
  if (typeof x !== "number" || typeof y !== "number" || !Number.isFinite(x) || !Number.isFinite(y)) return null;
  if (x < 0 || y < 0 || x > 2000 || y > 2000) return null;
  return { x, y };
}

export function loadTouchPrefs(): TouchPrefs {
  let raw: unknown = null;
  try {
    const text = globalThis.localStorage?.getItem(STORAGE_KEY);
    raw = text ? JSON.parse(text) : null;
  } catch {
    raw = null;
  }
  if (!raw || typeof raw !== "object") return { ...DEFAULT_TOUCH_PREFS };
  const r = raw as Partial<Record<keyof TouchPrefs, unknown>>;
  const size = typeof r.size === "number" && Number.isFinite(r.size) ? r.size : 1;
  return {
    size: Math.min(SIZE_MAX, Math.max(SIZE_MIN, size)),
    leftHanded: r.leftHanded === true,
    vibration: r.vibration !== false,
    fieldTouch: r.fieldTouch !== false,
    kick: validSpot(r.kick),
    dash: validSpot(r.dash),
  };
}

let cached: TouchPrefs | null = null;
const listeners = new Set<(prefs: TouchPrefs) => void>();

export function touchPrefs(): TouchPrefs {
  cached ??= loadTouchPrefs();
  return cached;
}

export function saveTouchPrefs(prefs: TouchPrefs): void {
  cached = {
    ...prefs,
    size: Math.min(SIZE_MAX, Math.max(SIZE_MIN, prefs.size)),
  };
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(cached));
  } catch {
    /* sin almacenamiento (privado): vale para esta sesion */
  }
  for (const fn of listeners) fn(cached);
}

export function onTouchPrefsChange(fn: (prefs: TouchPrefs) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export interface ButtonBox {
  /** Centro, en pixeles de la ventana. */
  cx: number;
  cy: number;
  /** Diametro visible. */
  size: number;
}

/**
 * Lo que la pantalla no deja usar: el notch y la barra de inicio del iPhone en
 * horizontal (`env(safe-area-inset-*)`, con `viewport-fit=cover`). Los botones
 * se miden desde el borde UTIL: sin esto, con el notch a la derecha el boton de
 * golpear perdia 47 px de sitio para apuntar arrastrando.
 */
export interface SafeInsets {
  left: number;
  right: number;
  bottom: number;
}

const NO_INSETS: SafeInsets = { left: 0, right: 0, bottom: 0 };

export interface TouchLayout {
  kick: ButtonBox;
  dash: ButtonBox;
  /** La zona del joystick: de `stickFrom` a `stickTo` en x (pixeles). */
  stickFrom: number;
  stickTo: number;
}

/**
 * Donde cae cada boton en ESTA pantalla.
 *
 * Se recorta para que ningun boton se salga ni invada la mitad del joystick:
 * una posicion guardada en una tablet no puede dejar el boton fuera de la
 * pantalla del movil.
 */
export function layoutTouch(
  width: number,
  height: number,
  prefs: TouchPrefs,
  insets: SafeInsets = NO_INSETS,
): TouchLayout {
  const u = thumbUnit(width, height);
  const side = prefs.leftHanded ? insets.left : insets.right;
  const place = (spot: ButtonSpot, diameter: number): ButtonBox => {
    const size = diameter * u * prefs.size;
    const half = size / 2;
    const fromSide = Math.min(Math.max(side + spot.x * u, side + half + 4), width * (1 - STICK_ZONE) - half);
    const fromBottom = Math.min(Math.max(insets.bottom + spot.y * u, insets.bottom + half + 4), height - half - 4);
    return {
      cx: prefs.leftHanded ? fromSide : width - fromSide,
      cy: height - fromBottom,
      size,
    };
  };
  const stickWidth = width * STICK_ZONE;
  return {
    kick: place(prefs.kick ?? DEFAULT_KICK_SPOT, KICK_SIZE),
    dash: place(prefs.dash ?? DEFAULT_DASH_SPOT, DASH_SIZE),
    stickFrom: prefs.leftHanded ? width - stickWidth : 0,
    stickTo: prefs.leftHanded ? width : stickWidth,
  };
}

/** Lo contrario de `layoutTouch`: el centro arrastrado, a unidades de pulgar. */
export function spotFromPoint(
  cx: number,
  cy: number,
  width: number,
  height: number,
  leftHanded: boolean,
  insets: SafeInsets = NO_INSETS,
): ButtonSpot {
  const u = thumbUnit(width, height);
  const side = leftHanded ? insets.left : insets.right;
  return {
    x: Math.max(0, ((leftHanded ? cx : width - cx) - side) / u),
    y: Math.max(0, (height - cy - insets.bottom) / u),
  };
}

/** Las zonas seguras de ESTA pantalla, leidas del CSS (0 donde no hay). */
export function readSafeInsets(): SafeInsets {
  if (typeof document === "undefined" || !document.body) return NO_INSETS;
  const probe = document.createElement("div");
  probe.style.cssText =
    "position:fixed;visibility:hidden;pointer-events:none;" +
    "padding:0 env(safe-area-inset-right,0px) env(safe-area-inset-bottom,0px) env(safe-area-inset-left,0px)";
  document.body.appendChild(probe);
  const cs = getComputedStyle(probe);
  const px = (v: string): number => Number.parseFloat(v) || 0;
  const insets = { left: px(cs.paddingLeft), right: px(cs.paddingRight), bottom: px(cs.paddingBottom) };
  probe.remove();
  return insets;
}

export interface StickState {
  anchorX: number;
  anchorY: number;
  /** Direccion unitaria, o (0,0) dentro de la zona muerta. */
  moveX: number;
  moveY: number;
  /** Desplazamiento del pomo respecto al ancla, en pixeles. */
  knobX: number;
  knobY: number;
}

/**
 * El joystick que SIGUE al pulgar.
 *
 * El de antes se quedaba clavado donde apoyabas el dedo. Si el pulgar se iba
 * 150 px a la derecha, para correr a la izquierda habia que desandar esos 150
 * px hasta cruzar el ancla: el giro llegaba tarde justo en el regate, que es
 * donde un giro tarde cuesta el balon. Ahora, cuando el pulgar pasa del borde,
 * el ancla le va detras (como Brawl Stars o cualquier shooter de movil): el
 * cambio de sentido siempre esta a un radio de distancia, nunca a mas.
 */
export function followStick(
  anchorX: number,
  anchorY: number,
  pointX: number,
  pointY: number,
  radius: number,
  deadZone: number,
): StickState {
  let dx = pointX - anchorX;
  let dy = pointY - anchorY;
  const dist = Math.hypot(dx, dy);
  if (dist > radius) {
    const k = (dist - radius) / dist;
    anchorX += dx * k;
    anchorY += dy * k;
    dx = pointX - anchorX;
    dy = pointY - anchorY;
  }
  const reach = Math.min(dist, radius);
  if (reach < deadZone * radius || dist < 1e-6) {
    return { anchorX, anchorY, moveX: 0, moveY: 0, knobX: dx, knobY: dy };
  }
  return { anchorX, anchorY, moveX: dx / Math.hypot(dx, dy), moveY: dy / Math.hypot(dx, dy), knobX: dx, knobY: dy };
}
